import api, { methods } from "@api";
import Spacer from "@components/Spacer";
import Button from "@components/button";
import HeaderDetail from "@components/header-detail";
import PostcodeCheck from "@components/postcode-check";
import TextInput from "@components/text-input";
import AuthImage from "@features/auth/auth-image";
import ScreenScrollView from "@features/scroll-view/screen-scroll-view";
import { useMutation } from "@tanstack/react-query";
import { looksLikeMail, postCodeInputHelper, useOrientation } from "@utils";
import { Container, Text, View } from "dripsy";
import * as React from "react";
import { Platform } from "react-native";
import { useFormState } from "react-native-use-form";

import Error from "../error/error";
import HelperText from "@components/helper-text";
import { useNavigation } from "react-native-ridge-navigation";
import { useStoryBlokStory } from "@provider/storyblok-provider";
import type { AuthStoryblok } from "@type/storyblock-types";

function CreateAccountScreen() {
  const orientation = useOrientation();

  return (
    <>
      <View
        sx={{
          backgroundColor: "$secondaryBackground",
          flex: 1,
        }}
      >
        <ScreenScrollView avoidKeyboard>
          {Platform.OS !== "web" ? <HeaderDetail hasImage withBottomBorder={false} /> : null}
          {Platform.OS === "web" ? (
            <>
              <Spacer size="$6" />
              <Spacer size="$6" />
            </>
          ) : (
            <Spacer />
          )}
          <Container variant="small" style={{ alignSelf: "center" }}>
            <CreateAccount />
          </Container>
        </ScreenScrollView>
        {orientation === "PORTRAIT" ? <AuthImage /> : null}
      </View>
    </>
  );
}

export function CreateAccount() {
  const content = useStoryBlokStory<AuthStoryblok>("account");

  const { pop } = useNavigation();
  const [showSuccessState, setShowSuccessState] = React.useState(false);
  const [showZipcodeCheck, setZipcodeCheck] = React.useState(false);

  const [form, inputs] = useFormState(
    process.env.NODE_ENV === "development"
      ? {
          postalCode: "",
          email: "",
        }
      : { postalCode: "", email: "" },
    {
      onSubmit: ({ email, postalCode: zipcode }) => {
        registerRequest({ email, zipcode });
      },
    }
  );

  const {
    mutate: registerRequest,
    isPending,
    isError,
    error,
  } = useMutation({
    mutationFn: (data: { email: string; zipcode: string }) =>
      api<{ status: string }>({
        path: "/registratie-aanvraag",
        method: methods.POST,
        body: { ...data, isInAppRequest: true },
      }),
    onSuccess: (res) => {
      setShowSuccessState(true);
    },
  });

  return (
    <>
      {showSuccessState ? (
        <>
          <Text sx={{ pt: "$2", pb: "$3", color: "$text" }}>
            Je aanvraag is verstuurd. Binnen enkele minuten ontvang je een e-mail met een beveiligde link om een account
            aan te maken. Heb je niets ontvangen? Controleer je spambox. Probeer het vervolgens nog eens of neem contact
            met ons op.
          </Text>
          <Button onPress={pop}>Ga terug</Button>
        </>
      ) : (
        <>
          <TextInput
            color="$secondaryBackground"
            {...inputs.postalCode("postalCode", {
              required: true,
              label: "Postcode afleveradres",
              ...postCodeInputHelper,
              onBlur: () => setZipcodeCheck(true),
              onChangeText: (v) => v.length === 6 && setZipcodeCheck(true),
            })}
          />
          <HelperText visible type="info">
            Voor België voer de 4 cijfers in van je postcode, voor Nederland voer de 4 cijfers én de 2 letters van je
            postcode in.
          </HelperText>
          {showZipcodeCheck ? (
            <>
              <PostcodeCheck postcode={form.values.postalCode} />
              <Spacer />
            </>
          ) : null}
          <Error isError={form.hasError("postalCode")} error={form.errors.postalCode} paddingBottom="$2" />

          <TextInput
            color="$secondaryBackground"
            {...inputs.email("email", {
              required: true,
              label: "E-mailadres",
              validate: (v) => {
                return looksLikeMail(v) ? true : "E-mailadres moet @ en . bevatten";
              },
            })}
            onSubmitEditing={form.submit}
          />
          <Error isError={form.hasError("email")} error={form.errors.email} paddingBottom="$2" />
          <Error isError={isError} error={(error as any)?.message} paddingBottom="$2" />
          <Button onPress={form.submit}>{isPending ? `Aan het laden...` : `Account aanmaken`}</Button>
          <Text
            sx={{
              color: "$textSecondary",
              fontWeight: "500",
              fontSize: 12,
              marginTop: "$2",
              lineHeight: 18,
              letterSpacing: 0.3,
            }}
          >
            {content.information}
          </Text>
        </>
      )}
    </>
  );
}

export default CreateAccountScreen;
